import { useCallback, useSyncExternalStore } from 'react';

/**
 * Whether the person switched on "Your docs" beside the assistant's composer: every message they
 * send then asks the assistant to search the workspace's own help articles first. Off by default.
 * A request, not an authority: the server's search tool still checks the feature and membership.
 *
 * One value for the tab, shared by the docked panel and the full page (they are one conversation),
 * kept in sessionStorage so it survives navigation, and tied to the workspace it was switched on
 * in so it never follows the person into another one.
 */
const STORAGE_KEY = 'revvly.agent.searchOwnDocs';

function currentTenantId(): string | null {
  try {
    return localStorage.getItem('selectedTenantId') || sessionStorage.getItem('selectedTenantId');
  } catch {
    return null;
  }
}

const listeners = new Set<() => void>();

/** True when "Your docs" is on in the current workspace. */
export function readSearchOwnDocs(): boolean {
  try {
    return sessionStorage.getItem(STORAGE_KEY) === `${currentTenantId()}`;
  } catch {
    return false;
  }
}

export function setSearchOwnDocs(on: boolean): void {
  try {
    if (on) sessionStorage.setItem(STORAGE_KEY, `${currentTenantId()}`);
    else sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage may be unavailable (privacy mode): the choice then lasts no longer than this call.
  }
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** The switch, kept in step across the panel and the full page. */
export function useSearchOwnDocs(): [boolean, (on: boolean) => void] {
  const on = useSyncExternalStore(subscribe, readSearchOwnDocs, () => false);
  const set = useCallback((next: boolean) => setSearchOwnDocs(next), []);
  return [on, set];
}
